import { Box, Image, Link } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import SocialMediaLinks from "./SocialMediaLinks";

/**
 * The shell around the sign-in and sign-up forms. It carries the same design
 * language as storkplus.com — brand blue, Cairo display type and the dark
 * marketing palette — with the pitch on the left and the form on the right.
 * The forms themselves are untouched; `styles/auth.scss` restyles their fields.
 */
export const AuthWrapper = ({ header, subtitle, children, locked = false, hero = null, heroTitle, heroText }) => {
  const { t } = useTranslation();

  const points = [
    t("authPanel.point1", "Unlimited products and orders on the free plan"),
    t("authPanel.point2", "A Messenger AI assistant that replies and sells 24/7"),
    t("authPanel.point3", "Arabic and English storefront, ready out of the box"),
    t("authPanel.point4", "No commission on your sales — ever"),
  ];

  return (
    // `hero`: replaces the bullet points with custom content (the feature
    // slider on the Google signup pages) on a brand-blue panel that also
    // shows on phones.
    <Box className={`sp-auth${hero ? " sp-auth--hero" : ""}`}>
      <Box className="sp-auth-brand">
        <Box className="sp-auth-brand-top">
          {locked ? (
            // Blocking steps (Google signup details): no way out of the page.
            <Box className="sp-auth-logo">
              <Image src="/logo-text-white.png" alt="StorkPlus" />
            </Box>
          ) : (
            <Link href="https://storkplus.com/" isExternal className="sp-auth-logo">
              <Image src="/logo-text-white.png" alt="StorkPlus" />
            </Link>
          )}
          <LanguageSwitcher />
        </Box>

        <Box className="sp-auth-brand-body">
          <span className="sp-auth-eyebrow">
            {t("authPanel.eyebrow", "Start free — no credit card")}
          </span>
          <h1>{heroTitle || t("authPanel.title", "Your smart store, live in minutes")}</h1>
          <p>
            {heroText ||
              t(
                "authPanel.subtitle",
                "Sell online, answer every Messenger message automatically and keep fake orders out — from one dashboard."
              )}
          </p>

          {hero || (
            <ul className="sp-auth-points">
              {points.map((point) => (
                <li key={point}>
                  <span className="sp-auth-tick">✓</span>
                  <span>{point}</span>
                </li>
              ))}
            </ul>
          )}
        </Box>

        {locked ? null : (
          <Box className="sp-auth-brand-foot">
            <SocialMediaLinks />
          </Box>
        )}
      </Box>

      <Box className="sp-auth-panel">
        <Box className="sp-auth-card">
          <h2>{header}</h2>
          {subtitle ? <p className="sp-auth-sub">{subtitle}</p> : null}
          {children}
        </Box>
      </Box>
    </Box>
  );
};
